import forge from 'node-forge';
import CryptoJS from 'crypto-js';
import { Buffer } from 'buffer';
import { getPortalServerIp } from '../storage';
import { IdentificationId } from './util';
import api from './apiPortal';
// ########## RSA DECRYPTION METHODS ##########

const GenerateRsaKeys = async () => {
  try {
    const keyPair = await window.crypto.subtle.generateKey(
      {
        name: 'RSA-OAEP',
        modulusLength: 4096, // 4096-bit key
        publicExponent: new Uint8Array([1, 0, 1]), // 65537 exponent
        hash: 'SHA-256',
      },
      true, // extractable
      ['encrypt', 'decrypt'] // encryption and decryption
    );

    // Export the public key in SPKI format
    const publicKey = await window.crypto.subtle.exportKey(
      'spki',
      keyPair.publicKey
    );

    // Export the private key in PKCS8 format
    const privateKey = await window.crypto.subtle.exportKey(
      'pkcs8',
      keyPair.privateKey
    );

    // Convert the exported keys to PEM format
    const publicKeyPem = arrayBufferToPem(publicKey, 'PUBLIC KEY');
    const privateKeyPem = arrayBufferToPem(privateKey, 'PRIVATE KEY');

    return { private: privateKeyPem, public: publicKeyPem };
  } catch (error) {
    console.log(error);
  }
};

// Helper function to convert ArrayBuffer to PEM format
const arrayBufferToPem = (arrayBuffer, label) => {
  const base64 = arrayBufferToBase64(arrayBuffer);
  const lines = base64.match(/.{1,64}/g).join('\n');
  return `-----BEGIN ${label}-----\n${lines}\n-----END ${label}-----`;
};

// Helper function to convert ArrayBuffer to Base64
const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  const base64 = btoa(String.fromCharCode(...bytes));
  return base64;
};

const SetRSAKey = async () => {
  try {
    const identificationId = IdentificationId();

    let privateKey = localStorage.getItem(
      `privateRSAKey-${identificationId.identificationId}`
    );
    let publicKey = localStorage.getItem(
      `publicRSAKey-${identificationId.identificationId}`
    );

    if (!privateKey || !publicKey) {
      const keys = await GenerateRsaKeys();
      publicKey = keys.public;

      localStorage.setItem(
        `privateRSAKey-${identificationId.identificationId}`,
        keys.private
      );
      localStorage.setItem(
        `publicRSAKey-${identificationId.identificationId}`,
        keys.public
      );
      await SetEncryptionKey(publicKey, identificationId.identificationId);
    }

    const data = {
      identificationId,
      publicKey,
    };
    return data;
  } catch (error) {
    console.log(error);
  }
};

const RsaDecryptor = async (encrypted) => {
  try {
    const identificationId = IdentificationId();
    const privateKeyPem = localStorage.getItem(
      `privateRSAKey-${identificationId.identificationId}`
    );
    if (!privateKeyPem) throw new Error('Private key not found');

    const privateKey = forge.pki.privateKeyFromPem(privateKeyPem);
    const decrypted = privateKey.decrypt(
      forge.util.decode64(encrypted),
      //'RSA-OAEP'
      'RSAES-PKCS1-V1_5' // ← match the backend padding
    );

    return decrypted;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

// ########## AES Encryption Methods ##########

const EncryptMessage = async (message, key, iv) => {
  try {
    if (!key || !iv) return message;

    const keyParse = CryptoJS.enc.Base64.parse(key);
    const ivParse = CryptoJS.enc.Base64.parse(iv);
    const encrypted = CryptoJS.AES.encrypt(message, keyParse, {
      iv: ivParse,
    }).toString();

    return `ENC:${encrypted}`;
  } catch (error) {
    console.log(error);
  }
};

const DecryptMessage = async (message, iv, key) => {
  try {
    if (!message.startsWith('ENC:')) return message;

    const keyParse = CryptoJS.enc.Base64.parse(key);
    const ivParse = CryptoJS.enc.Base64.parse(iv);
    const bytes = CryptoJS.AES.decrypt(message.slice(4), keyParse, {
      iv: ivParse,
    });

    return bytes.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.log(error);
  }
};

const EncryptFile = async (base64, key, iv) => {
  try {
    if (!key || !iv) return base64;

    const keyParse = CryptoJS.enc.Base64.parse(key);
    const ivParse = CryptoJS.enc.Base64.parse(iv);
    const encrypted = CryptoJS.AES.encrypt(base64, keyParse, {
      iv: ivParse,
    }).toString();

    return btoa(`ENC:${encrypted}`);
  } catch (error) {
    console.log(error);
  }
};

const DecryptFile = async (encrypted, iv, key) => {
  try {
    if (!encrypted) return encrypted;
    // Strip WCF JSON-wrapped quotes or surrounding whitespace from raw string
    const cleanEncrypted = encrypted.trim().replace(/^"|"$/g, '');
    const base64 = atob(cleanEncrypted);
    if (!base64.startsWith('ENC:')) return encrypted;

    const keyParse = CryptoJS.enc.Base64.parse(key);
    const ivParse = CryptoJS.enc.Base64.parse(iv);
    const bytes = CryptoJS.AES.decrypt(base64.slice(4), keyParse, {
      iv: ivParse,
    });

    return bytes.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.log(error);
  }
};

const SetEncryptionKey = async (publicKey, identificationId) => {
  api.defaults.baseURL = getPortalServerIp();
  let data = {};
  data.identificationId = identificationId;
  data.versionNumber = '1.0.61';
  data.appType = 4000;
  data.platformType = 7;
  data.publicKey = publicKey;
  return api.post('/PortalStaff/ios/SetEncryptionKey', data).then((res) => {
    return res.data;
  });
};

const GetE2EEncryptionStatus = async (identificationId) => {
  api.defaults.baseURL = getPortalServerIp();
  let data = {};
  data.identificationId = identificationId;
  data.versionNumber = '1.0.61';
  data.appType = 4000;
  data.platformType = 7;
  return api
    .post('/PortalStaff/ios/GetE2EEncryptionStatus', data)
    .then((res) => res.data);
};

// Convert ArrayBuffer to CryptoJS WordArray
const arrayBufferToWordArray = (ab) => {
  const i8a = new Uint8Array(ab);
  const a = [];
  for (let i = 0; i < i8a.length; i += 4) {
    a.push(
      (i8a[i] << 24) | (i8a[i + 1] << 16) | (i8a[i + 2] << 8) | i8a[i + 3]
    );
  }
  return CryptoJS.lib.WordArray.create(a, i8a.length);
};

// Convert CryptoJS WordArray to Uint8Array
const wordArrayToUint8Array = (wordArray) => {
  const l = wordArray.sigBytes;
  const words = wordArray.words;
  const result = new Uint8Array(l);
  for (let i = 0; i < l; i++) {
    result[i] = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
  }
  return result;
};

// Encrypt raw binary ArrayBuffer using AES-CBC in browser
const EncryptAttachmentBrowser = async (arrayBuffer, ivBase64, keyBase64) => {
  try {
    const keyParse = CryptoJS.enc.Base64.parse(keyBase64);
    const ivParse = CryptoJS.enc.Base64.parse(ivBase64);
    const wordArray = arrayBufferToWordArray(arrayBuffer);

    const encrypted = CryptoJS.AES.encrypt(wordArray, keyParse, {
      iv: ivParse,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    return wordArrayToUint8Array(encrypted.ciphertext);
  } catch (error) {
    console.error('Attachment Encryption Error:', error);
    return null;
  }
};

// Decrypt raw binary ArrayBuffer using AES-CBC in browser
const DecryptAttachmentBrowser = async (arrayBuffer, ivBase64, keyBase64) => {
  try {
    const keyParse = CryptoJS.enc.Base64.parse(keyBase64);
    const ivParse = CryptoJS.enc.Base64.parse(ivBase64);
    const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer);

    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: wordArray },
      keyParse,
      {
        iv: ivParse,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      }
    );

    return wordArrayToUint8Array(decrypted);
  } catch (error) {
    console.error('Attachment Decryption Error:', error);
    return null;
  }
};

export {
  SetRSAKey,
  RsaDecryptor,
  EncryptMessage,
  DecryptMessage,
  EncryptFile,
  DecryptFile,
  GenerateRsaKeys,
  GetE2EEncryptionStatus,
  SetEncryptionKey,
  wordArrayToUint8Array,
  EncryptAttachmentBrowser,
  DecryptAttachmentBrowser,
};
